import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { fetchConfig, onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, QuantitySelectorUpdateEvent, CartUpdateEvent } from '@theme/events';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;
/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} liveRegion - The live region.
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */

class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar'];

  /**
   * Selectors for every "Add to Cart" element across the PDP that should
   * suppress the sticky bar while visible. The sticky bar is shown by
   * default and only hidden when AT LEAST ONE of these is in the viewport.
   *
   * Includes:
   *  - product-form-buttons (main buy buttons block)
   *  - purchase-in-bulk-component
   *  - buy-more-save-more-component
   *  - complete-your-look-component
   *  - buy-the-bundle-component
   *  - dy-reco-section (dynamically injected by DY platform)
   *
   * @type {string[]}
   */
  static ATC_VISIBILITY_SELECTORS = [
    '.buy-buttons-block',
    'purchase-in-bulk-component .purchase-in-bulk__add-to-cart',
    'buy-more-save-more-component .bmsm__add-to-cart',
    'complete-your-look-component .complete-your-look__add-to-cart',
    'buy-the-bundle-component .buy-the-bundle__add-to-cart',
    '.dy-reco-section .dy-recommendation-item__add-to-cart',
  ];

  /** Selector for the section that gates the desktop reveal. */
  static DESKTOP_REVEAL_AFTER_SELECTOR = '.product-accordion';

  /** Viewport width (px) at and above which the desktop reveal gate applies. */
  static DESKTOP_MIN_WIDTH = 1024;

  /** @type {IntersectionObserver | null} */
  #atcButtonsIntersectionObserver = null;

  /** @type {MutationObserver | null} */
  #dynamicAtcMutationObserver = null;

  /**
   * Tracks every observed ATC element currently intersecting the viewport.
   * Sticky bar hides when this set is non-empty.
   * @type {Set<Element>}
   */
  #visibleAtcElements = new Set();

  /**
   * Avoids re-observing the same element multiple times. WeakSet so removed
   * DOM nodes are garbage-collected automatically.
   * @type {WeakSet<Element>}
   */
  #observedAtcElements = new WeakSet();

  /** @type {number | undefined} */
  #timeout;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #atcObserveScheduled = false;

  /** @type {boolean} */
  #resizeScheduled = false;

  /**
   * Desktop only: observes the product-accordion section so the sticky bar can
   * be revealed once it scrolls into view.
   * @type {IntersectionObserver | null}
   */
  #accordionObserver = null;

  /**
   * Desktop only: true once the product-accordion section has reached the
   * viewport (or been scrolled past). The desktop sticky bar stays hidden
   * until this is true, so it is hidden by default on page load.
   * @type {boolean}
   */
  #accordionReached = false;

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#atcButtonsIntersectionObserver?.disconnect();
    this.#dynamicAtcMutationObserver?.disconnect();
    this.#accordionObserver?.disconnect();
    this.#visibleAtcElements.clear();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */(timeoutId) => clearTimeout(timeoutId));
    }
    // Ensure the header class doesn't get left behind when the component unmounts.
    this.#toggleHeaderStuckClass(false);
  }

  /**
   * Sets up observers that drive the sticky bar visibility.
   *
   * Mobile: the bar is shown whenever no tracked Add to Cart button is in the
   * viewport (the main buy buttons scroll away naturally).
   *
   * Desktop (>= {@link DESKTOP_MIN_WIDTH}px): the main buy buttons live in a
   * `position: sticky` column and stay pinned, so the bar is instead hidden by
   * default on load and only revealed once the product-accordion section has
   * reached the viewport — and still only while no tracked ATC section is on
   * screen. It then stays visible all the way down to the footer.
   *
   * Tracked sections (see {@link ATC_VISIBILITY_SELECTORS}):
   *   product-form-buttons, purchase-in-bulk-component,
   *   buy-more-save-more-component, complete-your-look-component,
   *   buy-the-bundle-component, dy-reco-section.
   */
  #setupIntersectionObserver() {
    // Observe every ATC button across the PDP. When any of them is on screen
    // the sticky bar is hidden; when none are visible, it is shown.
    this.#atcButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          this.#visibleAtcElements.add(entry.target);
        } else {
          this.#visibleAtcElements.delete(entry.target);
        }
      }
      this.#updateStickyBarVisibility();
    });

    this.#observeAllAtcButtons();

    // DY recommendations (and other late-mounted blocks) inject their ATC
    // buttons after page load. Watch the body for newly-added nodes and start
    // observing any new ATCs that match our selectors.
    this.#dynamicAtcMutationObserver = new MutationObserver(() => {
      if (this.#atcObserveScheduled) return;
      this.#atcObserveScheduled = true;
      requestAnimationFrame(() => {
        this.#atcObserveScheduled = false;
        this.#observeAllAtcButtons();
      });
    });
    this.#dynamicAtcMutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });

    this.#setupAccordionObserver();

    // Re-evaluate when crossing the desktop/mobile breakpoint, since the gate
    // differs between the two and no observer fires on a pure resize.
    window.addEventListener('resize', this.#onResize, { signal: this.#abortController.signal });

    this.#targetAddToCartButton = this.querySelector('.sticky-add-to-cart__buttons [ref="addToCartButton"]');
  }

  #onResize = () => {
    if (this.#resizeScheduled) return;
    this.#resizeScheduled = true;
    requestAnimationFrame(() => {
      this.#resizeScheduled = false;
      this.#updateStickyBarVisibility();
    });
  };

  /**
   * Desktop gate: watch the product-accordion section. The sticky bar stays
   * hidden by default on load and is only revealed once the accordion has
   * reached the viewport (or been scrolled past). `boundingClientRect.top < 0`
   * keeps the gate open after the accordion scrolls above the viewport, so the
   * bar stays eligible all the way down the page (it still hides whenever a
   * tracked ATC section is on screen).
   */
  #setupAccordionObserver() {
    const accordion = document.querySelector(StickyAddToCartComponent.DESKTOP_REVEAL_AFTER_SELECTOR);
    if (!accordion) return;

    this.#accordionObserver = new IntersectionObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;

      this.#accordionReached = entry.isIntersecting || entry.boundingClientRect.top < 0;
      this.#updateStickyBarVisibility();
    });

    this.#accordionObserver.observe(accordion);
  }

  /**
   * Finds every ATC element matching the configured selectors and starts
   * observing any that we are not already tracking. Safe to call repeatedly;
   * IntersectionObserver.observe() is a no-op for already-observed elements
   * and the WeakSet guards against extra work.
   */
  #observeAllAtcButtons() {
    if (!this.#atcButtonsIntersectionObserver) return;

    const selector = StickyAddToCartComponent.ATC_VISIBILITY_SELECTORS.join(',');
    const elements = document.querySelectorAll(selector);

    elements.forEach((el) => {
      // Skip the sticky bar's own ATC button so it doesn't toggle itself.
      if (this.contains(el)) return;
      if (this.#observedAtcElements.has(el)) return;

      this.#observedAtcElements.add(el);
      this.#atcButtonsIntersectionObserver?.observe(el);
    });
  }

  /**
   * Reconciles sticky bar visibility against the current set of in-viewport
   * ATC elements. The bar shows whenever no tracked ATC is on screen — which
   * means it appears before the user reaches the product-accordion section
   * (as soon as the main buy buttons leave the viewport) and stays visible
   * all the way down to the footer.
   */
  #updateStickyBarVisibility() {
    // Prune any tracked elements that have been removed from the DOM (e.g.
    // when DY re-renders its recommendations) so they don't keep the bar hidden.
    for (const el of this.#visibleAtcElements) {
      if (!el.isConnected) this.#visibleAtcElements.delete(el);
    }

    // No tracked ATC section is on screen.
    const noAtcVisible = this.#visibleAtcElements.size === 0;
    const isDesktop = window.innerWidth >= StickyAddToCartComponent.DESKTOP_MIN_WIDTH;

    // Desktop: hidden by default on load; only reveal once the product-accordion
    // has reached the viewport AND no defined ATC section is on screen.
    // Mobile: reveal whenever no ATC section is on screen (buy buttons scroll
    // away naturally so no accordion gate is needed).
    const shouldShow = isDesktop ? noAtcVisible && this.#accordionReached : noAtcVisible;

    if (shouldShow && !this.#isStuck) {
      this.#showStickyBar();
    } else if (!shouldShow && this.#isStuck) {
      this.#hideStickyBar();
    }
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;

    const form = this.refs.addToCartButton.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const productForm = /** @type {any | null} */ (this.#getProductForm());
    const quantitySelector = productForm?.refs?.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        // Handle quantity validation error
        this.#handleQuantityValidationError(validation);
        return;
      }
    }

    // Only proceed with animation and submission if puppet flag is not set
    if (this.#targetAddToCartButton.dataset.puppet !== 'true') {
      this.#targetAddToCartButton.dataset.puppet = 'true';

      await this.animateAddToCart();
    }

    // Proceed with form submission
    await this.#submitForm(form);
  };

  /**
   * Handles quantity validation errors
   * @param {{canAdd: boolean, maxQuantity?: number}} validation - Validation result
   */
  #handleQuantityValidationError(validation) {
    const { addToCartTextError } = this.refs;

    // Disable the sticky add to cart button
    this.refs.addToCartButton.disabled = true;

    const errorTemplate = this.dataset.quantityErrorMax || '';
    const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');

    if (addToCartTextError) {
      addToCartTextError.classList.remove('hidden');

      const textNode = addToCartTextError.childNodes[2];
      if (textNode) {
        textNode.textContent = errorMessage;
      } else {
        const newTextNode = document.createTextNode(errorMessage);
        addToCartTextError.appendChild(newTextNode);
      }

      this.#setLiveRegionText(errorMessage);

      if (this.#timeout) clearTimeout(this.#timeout);
      this.#timeout = setTimeout(() => {
        if (!addToCartTextError) return;
        addToCartTextError.classList.add('hidden');
        this.#clearLiveRegionText();
      }, ERROR_MESSAGE_DISPLAY_DURATION);
    }

    setTimeout(() => {
      // Re-enable the sticky add to cart button
      this.refs.addToCartButton.disabled = false;
    }, ERROR_BUTTON_REENABLE_DELAY);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */(timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1);
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };

  /**
   * Submits the form to add item to cart
   * @param {HTMLFormElement} form - The form element to submit
   */
  async #submitForm(form) {
    const { addToCartTextError } = this.refs;
    if (this.#timeout) clearTimeout(this.#timeout);

    const formData = new FormData(form);
    const plainObject = Object.fromEntries(formData.entries());

    /** @type {Record<string, string>} */
    const lineItemProperties = {};
    for (const [key, value] of formData.entries()) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (!match || typeof value !== 'string') continue;

      // Ignore empty values so we only send fields selected by the customer.
      if (!value.trim()) continue;
      // @ts-ignore
      lineItemProperties[match[1]] = value;
    }

    const mainLineItem = {
      // @ts-ignore
      id: parseInt(plainObject.id),
      // @ts-ignore
      quantity: parseInt(plainObject.quantity) || Number(this.dataset.quantityDefault) || 1,
    };

    if (Object.keys(lineItemProperties).length > 0) {
      // @ts-ignore
      mainLineItem.properties = lineItemProperties;
    }

    // Resolve the product detail container (scoped to this section) so we can
    // look up the secure-shield and packaging-box state just like product-form.js.
    const sectionElement = this.closest('.shopify-section');
    const productDetailContainer =
      sectionElement?.querySelector("[data-id='product-information']") ?? null;

    // Add Loop plan if widget is selected
    const loopPlanWidget = productDetailContainer?.querySelector('.loop-widget-container');
    if (
      loopPlanWidget &&
      !loopPlanWidget
        .querySelector('.loop-w-btn-group-description-container')
        ?.classList.contains('loop-display-none')
    ) {
      // @ts-ignore
      const sellingPlan = loopPlanWidget.querySelector("select[name='selling_plan']")?.value || document.querySelector("select[name='selling_plan']")?.value;
      if (sellingPlan) {
        // @ts-ignore
        mainLineItem.selling_plan = parseInt(sellingPlan);
        // @ts-ignore
        mainLineItem.properties = {
          _product_type: 'subscription',
        };
      }
    }

    const items = {
      items: [mainLineItem],
    };

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      // @ts-ignore
      items['sections'] = cartItemComponentsSectionIds.join(',');
    });

    // Attach secure shield as a linked line item when the checkbox is checked,
    // mirroring the behaviour of product-form.js.
    const secure_shields =
      productDetailContainer?.querySelectorAll('.secure-shield-block') ?? [];

    const is_secureshield_checked = [...secure_shields].some((shield) =>
      // @ts-ignore
      shield.querySelector('input[name="secure_shield_protection"]')?.checked
    );

    if (secure_shields?.length > 0 && is_secureshield_checked) {
      const secureShieldInput = /** @type {HTMLInputElement | null} */ (
        productDetailContainer?.querySelector('input[name="secure_shield_protection"]') ?? null
      );
      let secureShieldId = parseInt(secureShieldInput?.value ?? '');
      // @ts-ignore
      let parentID = parseInt(plainObject.id);
      // @ts-ignore
      const updatedItems = items.items.map((item) => {
        // @ts-ignore
        if (item.id === parentID) {
          return {
            ...item,
            properties: {
              // @ts-ignore
              ...item.properties,
              // @ts-ignore
              _has_secureshield: true,
              _secureshield_id: secureShieldId,
            },
          };
        }
        return item;
      });

      items.items = updatedItems;

      items.items.push({
        // @ts-ignore
        id: secureShieldId,
        // @ts-ignore
        parent_id: parentID,
        // @ts-ignore
        quantity: parseInt(plainObject.quantity) || Number(this.dataset.quantityDefault) || 1,
        // @ts-ignore
        properties: {
          _product_type: 'secure_shield',
        },
      });
    }

    // Add packaging-box selection if an active option exists
    if (
      productDetailContainer?.querySelector('packaging-box') &&
      productDetailContainer?.querySelector('packaging-box packaging-box-item.active')
    ) {
      const activePackagingBoxItem = /** @type {HTMLElement | null} */ (
        productDetailContainer?.querySelector('packaging-box packaging-box-item.active') ?? null
      );
      items.items.push({
        // @ts-ignore
        id: parseInt(activePackagingBoxItem?.dataset.variantId ?? ''),
        quantity: 1,
      });
    }

    const fetchCfg = fetchConfig('json', { body: JSON.stringify(items) });
    fetch(Theme.routes.cart_add_url, fetchCfg)
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError || !(addToCartTextError instanceof HTMLElement)) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'sticky-add-to-cart',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          const addedText = Theme.translations?.added || 'Added to cart';
          this.#setLiveRegionText(addedText);

          setTimeout(() => {
            this.#clearLiveRegionText();
          }, SUCCESS_MESSAGE_DISPLAY_DURATION);

          await this.#fetchAndUpdateCartQuantity();

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'sticky-add-to-cart',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error('Add to cart error:', error);
      })
      .finally(() => {
        this.#handleCartAddComplete();
      });
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    if (liveRegion && liveRegion instanceof HTMLElement) {
      liveRegion.textContent = text;
    }
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    if (liveRegion && liveRegion instanceof HTMLElement) {
      liveRegion.textContent = '';
    }
  }

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }

    if (typeof window.yotpoWidgetsContainer !== 'undefined') {
      window.yotpoWidgetsContainer.initWidgets();
    }
    // Restore the current quantity display if needed
    // this.#updateButtonText();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */(option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   */
  #handleCartAddComplete = () => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    // this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
    this.#toggleHeaderStuckClass(true);
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
    this.#toggleHeaderStuckClass(false);
  }

  /**
   * Toggle a class on the page header whenever the sticky add-to-cart bar
   * is stuck/unstuck. Other components (e.g. header layout) can react to
   * this state via CSS.
   *
   * @param {boolean} isStuck - True when the sticky bar is visible/stuck.
   */
  #toggleHeaderStuckClass(isStuck) {
    const header = document.querySelector('#header-group');
    if (!header) return;
    header.classList.toggle('header-with-sticky-add-to-cart', isStuck);
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    // this.#updateButtonText();
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    this.#handleCartAddComplete();
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'sticky-add-to-cart') return;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartItem = cart.items.find((item) => item.variant_id.toString() === variantIdInput.value.toString());
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches cart and updates quantity selector for current variant
   * @returns {Promise<number>} The cart quantity for the current variant
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return 0;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      return this.#updateCartQuantityFromData(cart);
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return 0;
    }
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
